import { X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { Discipline } from "@/hooks/use-disciplines";
import type { CurrentPodMember, MappedRole, RequestLine } from "@/hooks/use-staffing-requests";
import { useDisciplineResources } from "@/hooks/use-staffing-requests";
import { StaffingStatusBadge } from "./StaffingStatusBadge";

const ROLES: MappedRole[] = ["R1", "R2", "R3", "R4", "Supporting"];

// Radix <SelectItem /> forbids an empty-string value — sentinel maps back to null.
const UNSET = "__unset__";

/**
 * Capacity points a Pod position consumes. R1 = 2 pts; all others = 1 pt.
 * Source hours are context only and never enter this calculation (firewall).
 */
function pointsForRole(role: MappedRole): number {
  return role === "R1" ? 2 : 1;
}

interface Props {
  line: RequestLine;
  disciplines: Discipline[];
  /** The linked Solution's ACTIVE team — drives per-line change hints. */
  currentPod: CurrentPodMember[];
  periodColumns: string[];
  onPatch: (payload: {
    lineId: string;
    mappedDisciplineId?: string | null;
    mappedRole?: MappedRole | null;
    mappedResourceId?: string | null;
  }) => void;
  isPending: boolean;
}

export function LineMappingRow({
  line,
  disciplines,
  currentPod,
  periodColumns,
  onPatch,
  isPending,
}: Props) {
  const effectiveRole = line.mappedRole ?? null;

  // Workflow 2 — change hint vs the current team. The role in play (mapped, else
  // suggested) may be held by an incumbent: a different Resource on this line is a
  // REPLACEMENT; the incumbent themselves is "current team"; no Resource means the
  // incumbent is kept (commit never unassigns for an unnamed line).
  const hintRole = line.mappedRole ?? line.suggestedRole;
  const incumbent =
    hintRole && hintRole !== "Supporting"
      ? (currentPod.find((m) => m.role === hintRole) ?? null)
      : null;
  const lineResourceName = line.mappedResourceName ?? line.sourceResourceName;
  const isIncumbent =
    incumbent !== null &&
    (line.mappedResourceId === incumbent.resourceId ||
      (line.mappedResourceId === null &&
        lineResourceName?.trim().toLowerCase() === incumbent.resourceName.trim().toLowerCase()));
  const changeHint: { tone: "same" | "replace" | "kept"; text: string } | null = incumbent
    ? isIncumbent
      ? { tone: "same", text: `Current team — ${incumbent.resourceName} stays at ${hintRole}` }
      : lineResourceName
        ? {
            tone: "replace",
            text: `${lineResourceName} replaces ${incumbent.resourceName} at ${hintRole}`,
          }
        : {
            tone: "kept",
            text: `${hintRole} held by ${incumbent.resourceName} — kept unless you assign a replacement`,
          }
    : null;

  // Build a period → hours lookup from this line's breakdown.
  const hoursByPeriod = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of line.sourcePeriods) {
      if (entry.period !== null) {
        map.set(entry.period, entry.hours);
      }
    }
    return map;
  }, [line.sourcePeriods]);

  const suggestionLabel = useMemo(() => {
    const disc = disciplines.find((d) => d.id === line.suggestedDisciplineId);
    if (!disc && !line.suggestedRole) return null;
    return [disc?.code, line.suggestedRole].filter(Boolean).join(" / ");
  }, [disciplines, line.suggestedDisciplineId, line.suggestedRole]);

  const isLocked = line.lineStatus === "Committed" || line.lineStatus === "Rejected";

  // The suggestion still differs from the RM's confirmed mapping in at least one
  // suggested part — the one-click Apply stays available until they agree.
  const suggestionUnapplied =
    (line.suggestedDisciplineId !== null &&
      line.mappedDisciplineId !== line.suggestedDisciplineId) ||
    (line.suggestedRole !== null && line.mappedRole !== line.suggestedRole);

  const pts = effectiveRole !== null ? pointsForRole(effectiveRole) : null;

  return (
    <tr className="border-t border-border">
      {/* Row index */}
      <td className="px-3 py-2.5 text-[10px] text-muted-foreground">{line.rowIndex + 1}</td>

      {/* Source data (read-only) */}
      <td className="px-3 py-2.5 min-w-[140px]">
        <div className="font-medium text-foreground">{line.sourceRoleLabel}</div>
        {line.sourceResourceName && (
          <div className="text-[10px] text-muted-foreground">{line.sourceResourceName}</div>
        )}
      </td>

      {/* Per-period hours columns (one cell per periodColumn) */}
      {periodColumns.map((col) => (
        <td key={col} className="px-3 py-2.5 text-right text-[11px] text-muted-foreground">
          {hoursByPeriod.has(col) ? (hoursByPeriod.get(col) ?? 0).toLocaleString() : "—"}
        </td>
      ))}

      {/* Total source hours */}
      <td className="px-3 py-2.5 text-right text-[11px] font-medium text-foreground">
        {line.sourceHours > 0 ? `${line.sourceHours.toLocaleString()} hrs` : "—"}
      </td>

      {/* System suggestion — one-click accept; picking different values in the
          selects is the override (AC: "suggested by upload — confirm"). */}
      <td className="px-3 py-2.5">
        {suggestionLabel ? (
          <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {suggestionLabel}
            {!isLocked && suggestionUnapplied && (
              <button
                type="button"
                disabled={isPending}
                onClick={() =>
                  onPatch({
                    lineId: line.id,
                    // Apply only the suggested parts; leave the rest untouched.
                    ...(line.suggestedDisciplineId
                      ? { mappedDisciplineId: line.suggestedDisciplineId }
                      : {}),
                    ...(line.suggestedRole ? { mappedRole: line.suggestedRole } : {}),
                    // A Resource picked under a DIFFERENT Discipline is stale once the
                    // suggestion applies — clear it (same rule as the Discipline select).
                    ...(line.suggestedDisciplineId &&
                    line.mappedResourceId &&
                    line.mappedDisciplineId !== line.suggestedDisciplineId
                      ? { mappedResourceId: null }
                      : {}),
                  })
                }
                className="rounded bg-foreground/90 px-1 py-0 text-[9px] font-semibold text-background hover:bg-foreground disabled:opacity-40"
                title="Suggested by upload — confirm to apply this Discipline mapping"
              >
                Apply
              </button>
            )}
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground/50">—</span>
        )}
      </td>

      {/* Editable: Discipline */}
      <td className="px-3 py-2.5">
        <Select
          value={line.mappedDisciplineId ?? UNSET}
          onValueChange={(v) => {
            if (isLocked) return;
            // Changing the Discipline invalidates a Resource picked under the old
            // one — the search is Discipline-scoped, so clear it.
            onPatch({
              lineId: line.id,
              mappedDisciplineId: v === UNSET ? null : v,
              ...(line.mappedResourceId ? { mappedResourceId: null } : {}),
            });
          }}
          disabled={isPending || isLocked}
        >
          <SelectTrigger className="h-7 w-48 text-[11px]">
            <SelectValue placeholder="Discipline" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNSET}>— None —</SelectItem>
            {disciplines.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name} ({d.code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>

      {/* Editable: Position — the RM's in-app decision (never on the artifact). */}
      <td className="px-3 py-2.5">
        <Select
          value={line.mappedRole ?? UNSET}
          onValueChange={(v) => {
            if (isLocked) return;
            onPatch({ lineId: line.id, mappedRole: v === UNSET ? null : (v as MappedRole) });
          }}
          disabled={isPending || isLocked}
        >
          <SelectTrigger className="h-7 w-28 text-[11px]">
            <SelectValue placeholder="Position" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNSET}>— None —</SelectItem>
            {ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>

      {/* Editable: Resource — a Discipline-scoped search box. Not pickable until a
          Discipline is populated; picking is OPTIONAL (a position may stay Open). */}
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          <ResourceSearchBox line={line} onPatch={onPatch} isPending={isPending || isLocked} />
          {/* Capacity points — shown once a Position and Resource are chosen */}
          {pts !== null && line.mappedResourceId && (
            <span
              className="shrink-0 text-[10px] text-muted-foreground"
              title="Capacity points this Pod position consumes (not source hours)"
            >
              · {pts} pt{pts !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        {/* A Resource named on the plan — surfaced as a suggestion to search for. */}
        {line.sourceResourceName && !line.mappedResourceId && (
          <div className="mt-0.5 text-[10px] text-muted-foreground">
            Plan names: {line.sourceResourceName}
          </div>
        )}
        {/* Change vs the current team, shown while mapping — not only at commit. */}
        {changeHint && (
          <div
            className={[
              "mt-0.5 text-[10px]",
              changeHint.tone === "same"
                ? "text-success-600 dark:text-success-400"
                : changeHint.tone === "replace"
                  ? "font-medium text-warning-600 dark:text-warning-400"
                  : "text-muted-foreground",
            ].join(" ")}
          >
            {changeHint.tone === "replace" && "⇄ "}
            {changeHint.text}
          </div>
        )}
      </td>

      {/* Line status */}
      <td className="px-3 py-2.5">
        <StaffingStatusBadge status={line.lineStatus} />
      </td>
    </tr>
  );
}

/**
 * Discipline-scoped Resource search. Disabled until the line has a Discipline; the
 * result list only contains ACTIVE Resources holding that Discipline. A picked
 * Resource renders as a chip with a clear (×) control.
 */
function ResourceSearchBox({
  line,
  onPatch,
  isPending,
}: {
  line: RequestLine;
  onPatch: (payload: { lineId: string; mappedResourceId?: string | null }) => void;
  isPending: boolean;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedSearch = useDebouncedValue(search, 250);

  const disciplineId = line.mappedDisciplineId;
  const { data: results, isLoading } = useDisciplineResources(
    open && disciplineId ? disciplineId : null,
    debouncedSearch,
  );

  const planName = line.sourceResourceName?.trim().toLowerCase() || null;

  // Picked state: show the chip, not the search box.
  if (line.mappedResourceId) {
    return (
      <span className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-muted/40 px-2 text-[11px] font-medium">
        {line.mappedResourceName ?? "Resource"}
        {!isPending && (
          <button
            type="button"
            onClick={() => onPatch({ lineId: line.id, mappedResourceId: null })}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground"
            aria-label="Clear Resource"
            title="Clear — leave the position Open"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  }

  return (
    <div className="relative">
      <input
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true); // typing always (re)opens the list
        }}
        onClick={() => setOpen(true)}
        onFocus={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current);
          setOpen(true);
        }}
        onBlur={() => {
          // Delay so a click on a result lands before the list unmounts.
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        placeholder={disciplineId ? "Search Resources…" : "Pick a Discipline first"}
        disabled={isPending || !disciplineId}
        title={
          disciplineId
            ? "Search active Resources holding this line's Discipline"
            : "A Resource can only be picked once the Discipline is populated"
        }
        className="h-7 w-40 rounded-md border border-border bg-card px-2 text-[11px] outline-none focus:border-foreground/40 disabled:cursor-not-allowed disabled:opacity-50"
      />
      {open && disciplineId && (
        <div className="absolute left-0 top-8 z-20 max-h-48 w-56 overflow-y-auto rounded-md border border-border bg-card shadow-md">
          {isLoading && (
            <div className="px-2 py-1.5 text-[11px] text-muted-foreground">Searching…</div>
          )}
          {!isLoading && (results ?? []).length === 0 && (
            <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
              No active Resources with this Discipline
              {debouncedSearch.trim() ? ` match “${debouncedSearch.trim()}”` : ""}.
            </div>
          )}
          {(results ?? []).map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onPatch({ lineId: line.id, mappedResourceId: r.id });
                setOpen(false);
                setSearch("");
              }}
              className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-[11px] hover:bg-muted"
            >
              {r.name}
              {planName && r.name.toLowerCase().includes(planName) && (
                <span className="rounded bg-success-100 px-1 text-[9px] font-medium text-success-700 dark:bg-success-900/40 dark:text-success-300">
                  from plan
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
